/**
 * PhoneCodeInput — v146.57
 *
 * Country-code dropdown + 10-digit phone box. Emits the concatenated
 * `+91XXXXXXXXXX` string via `onChange(fullE164)` so callers don't have
 * to reassemble the pieces.
 *
 * • Top-10 markets are pre-listed with flag emojis; "Other" opens a
 *   plain +NNN text input for anything else.
 * • Default code is `+91` (India) — user preference.
 * • Persists the last-picked code in `localStorage.linkolaPhoneCC`.
 */
import { useEffect, useState } from "react";

// Compact display: flag + dial-code only ("🇮🇳 +91"). The country
// name is dropped from the trigger to keep the dropdown narrow on
// mobile — the full name still appears in the dropdown options for
// scanning. v146.64.
const CODES = [
  { code: "+91",   short: "🇮🇳 +91",  label: "🇮🇳 +91 India" },
  { code: "+1",    short: "🇺🇸 +1",   label: "🇺🇸 +1 US / Canada" },
  { code: "+44",   short: "🇬🇧 +44",  label: "🇬🇧 +44 UK" },
  { code: "+971",  short: "🇦🇪 +971", label: "🇦🇪 +971 UAE" },
  { code: "+65",   short: "🇸🇬 +65",  label: "🇸🇬 +65 Singapore" },
  { code: "+61",   short: "🇦🇺 +61",  label: "🇦🇺 +61 Australia" },
  { code: "+49",   short: "🇩🇪 +49",  label: "🇩🇪 +49 Germany" },
  { code: "+33",   short: "🇫🇷 +33",  label: "🇫🇷 +33 France" },
  { code: "+81",   short: "🇯🇵 +81",  label: "🇯🇵 +81 Japan" },
  { code: "+86",   short: "🇨🇳 +86",  label: "🇨🇳 +86 China" },
  { code: "OTHER", short: "🌐 Other", label: "🌐 Other…" },
];

const DEFAULT_CC = "+91";

/**
 * Props:
 *   value:    the currently held phone string (e.g. "+919876543210")
 *   onChange: (fullPhone: string) => void
 *   testid:   root data-testid prefix (default "phone-code-input")
 *   autoFocus:boolean
 */
export default function PhoneCodeInput({ value = "", onChange, testid = "phone-code-input", autoFocus, disabled = false }) {
  const [cc, setCc] = useState(() => {
    // Try to parse existing `value`. Match longest known code first.
    if (value) {
      const sorted = CODES.filter((c) => c.code !== "OTHER").sort((a, b) => b.code.length - a.code.length);
      const found = sorted.find((c) => value.startsWith(c.code));
      if (found) return found.code;
      if (value.startsWith("+")) return "OTHER";
    }
    try {
      return localStorage.getItem("linkolaPhoneCC") || DEFAULT_CC;
    } catch (_) {
      return DEFAULT_CC;
    }
  });
  const [otherCc, setOtherCc] = useState(() => {
    if (value && value.startsWith("+")) {
      const known = CODES.find((c) => c.code !== "OTHER" && value.startsWith(c.code));
      if (!known) {
        const m = value.match(/^\+(\d{1,4})/);
        return m ? "+" + m[1] : "";
      }
    }
    return "";
  });
  const [national, setNational] = useState(() => {
    if (!value) return "";
    const known = CODES.filter((c) => c.code !== "OTHER").sort((a, b) => b.code.length - a.code.length)
      .find((c) => value.startsWith(c.code));
    if (known) return value.slice(known.code.length);
    const m = value.match(/^\+\d{1,4}(.*)$/);
    return m ? m[1] : value.replace(/^\+/, "");
  });

  useEffect(() => {
    try { localStorage.setItem("linkolaPhoneCC", cc === "OTHER" ? (otherCc || DEFAULT_CC) : cc); } catch (_) { /* noop */ }
  }, [cc, otherCc]);

  const emit = (nextNational, nextCc, nextOther) => {
    const codeStr = nextCc === "OTHER" ? (nextOther || "").trim() : nextCc;
    const digits = String(nextNational || "").replace(/\D/g, "");
    if (!digits) { onChange?.(""); return; }
    onChange?.(`${codeStr}${digits}`);
  };

  return (
    <div className="flex items-stretch gap-2 min-w-0" data-testid={testid}>
      <div className="relative shrink-0">
        <select
          value={cc}
          onChange={(e) => { setCc(e.target.value); emit(national, e.target.value, otherCc); }}
          disabled={disabled}
          data-testid={`${testid}-code`}
          className="border-2 border-ink bg-white pl-2 pr-6 py-2 text-sm font-mono appearance-none cursor-pointer h-full disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="Country code"
          title={CODES.find((c) => c.code === cc)?.label || cc}
        >
        {CODES.map((c) => (
          <option key={c.code} value={c.code}>{c.short}</option>
        ))}
        </select>
        {/* Faux chevron so the native arrow doesn't push the label off */}
        <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-ink/60 text-[10px]">▼</span>
      </div>
      {cc === "OTHER" && (
        <input
          type="text"
          value={otherCc}
          onChange={(e) => {
            let v = e.target.value.replace(/[^\d+]/g, "");
            if (v && !v.startsWith("+")) v = "+" + v;
            setOtherCc(v);
            emit(national, "OTHER", v);
          }}
          placeholder="+..."
          maxLength={5}
          data-testid={`${testid}-code-other`}
          className="border-2 border-ink px-2 py-2 text-sm font-mono w-16 shrink-0"
        />
      )}
      <input
        type="tel"
        inputMode="numeric"
        value={national}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, 15);
          setNational(v);
          emit(v, cc, otherCc);
        }}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder="9876543210"
        maxLength={15}
        data-testid={`${testid}-national`}
        className="border-2 border-ink px-3 py-2 text-sm font-mono flex-1 min-w-0 disabled:opacity-60"
      />
    </div>
  );
}
